import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataGrid, { Column, Toolbar, Item, SearchPanel } from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { fetchSFQList, createSFQ } from "../Redux/feasibility";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM } from "../../../utils/permissions";
import FeasibilityCreateModal from "./FeasibilityCreateModal";
import styled from "styled-components";

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.$bg || "#e9ecef"};
  color: ${(p) => p.$color || "#495057"};
`;

const VerBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e7f1ff;
  color: #0d6efd;
  font-weight: 600;
  font-size: 11px;
`;

const STATUS_STYLE = {
  Draft:          { bg: "#e9ecef", color: "#495057" },
  InternalReview: { bg: "#fff3cd", color: "#664d03" },
  Active:         { bg: "#d1ecf1", color: "#0c5460" },
  Finalized:      { bg: "#d1e7dd", color: "#0a3622" },
  Locked:         { bg: "#dee2e6", color: "#212529" },
  Archived:       { bg: "#f8d7da", color: "#721c24" },
};

export default function FeasibilityList({ onSelect }) {
  const dispatch    = useDispatch();
  const list        = useSelector((s) => s.feasibility.list);
  const loading     = useSelector((s) => s.feasibility.loading);
  const permissions = useSelector((s) => s.userInfo.permissions);

  // Only an owner-eligible employee (SFQ_STUDY_EDIT) may create a study now.
  const canCreate = hasPermission(permissions, PERM.SFQ_STUDY_EDIT);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    dispatch(fetchSFQList());
  }, [dispatch]);

  function renderStatus(cell) {
    const s = STATUS_STYLE[cell.value] || STATUS_STYLE.Draft;
    return <StatusBadge $bg={s.bg} $color={s.color}>{cell.value}</StatusBadge>;
  }

  function renderInvCount(cell) {
    const row = cell.data;
    if (!row.invitationCount) return <span style={{ color: "#adb5bd" }}>—</span>;
    return (
      <span style={{ fontSize: 12 }}>
        <span style={{ fontWeight: 600 }}>{row.invitationCount}</span>
        {row.submittedCount > 0 && (
          <span style={{ color: "#198754", marginLeft: 4 }}>
            ({row.submittedCount} submitted)
          </span>
        )}
      </span>
    );
  }

  // Studies created before the PM column exists have no PM assigned.
  function renderPerson(cell) {
    if (!cell.value) return <span style={{ color: "#adb5bd" }}>—</span>;
    return <span>{cell.value}</span>;
  }

  function renderVersion(cell) {
    if (cell.value == null) return <span style={{ color: "#adb5bd" }}>발행 전</span>;
    return <VerBadge title="마지막으로 발행된 버전입니다.">V{cell.value}</VerBadge>;
  }

  async function handleCreate(formData) {
    let result;
    try {
      result = await dispatch(createSFQ(formData)).unwrap();
    } catch (e) {
      // 대부분 Study Code 중복이다. 모달을 열어둔 채 코드만 고쳐 다시 시도할 수 있게 한다.
      alert(e?.message || "Study 생성에 실패했습니다.");
      return;
    }
    setShowCreate(false);
    dispatch(fetchSFQList());
    if (result?.id && onSelect) onSelect(result.id);
  }

  return (
    <>
      <DataGrid
        dataSource={list}
        keyExpr="id"
        showBorders
        rowAlternationEnabled
        hoverStateEnabled
        onRowDblClick={(e) => onSelect && onSelect(e.data.id)}
        loadPanel={{ enabled: loading }}
        style={{ fontSize: 13 }}
      >
        <SearchPanel visible />
        <Toolbar>
          {canCreate && (
            <Item location="before">
              <Button
                text="+ New SFQ"
                type="default"
                onClick={() => setShowCreate(true)}
                stylingMode="contained"
              />
            </Item>
          )}
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <Column dataField="studyCode"       caption="Study Code"  width={130} />
        <Column dataField="studyTitle"      caption="Study Title" minWidth={200} />
        <Column dataField="status"          caption="Status"      width={90}  cellRender={renderStatus} />
        <Column dataField="latestVersionNo" caption="Version"     width={90}  cellRender={renderVersion} />
        <Column dataField="invitationCount" caption="Sites"       width={140} cellRender={renderInvCount} />
        <Column dataField="createdByName"   caption="Owner"       width={110} cellRender={renderPerson} />
        <Column dataField="reviewerName"    caption="Reviewer"    width={110} cellRender={renderPerson} />
        <Column dataField="craNames"        caption="CRA"         width={160} cellRender={renderPerson} />
        <Column dataField="createdAt"       caption="Created"     dataType="datetime"
          format="yyyy-MM-dd" width={100} />
      </DataGrid>

      {showCreate && (
        <FeasibilityCreateModal
          onSubmit={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </>
  );
}
